// Realistic — but entirely FAKE — seed dataset for demo mode.
//
// Models ONE coherent jewellery business ("JewelFlow Demo Jewellers", Chennai):
// customers buy items, items come from suppliers (direct + on-approval), repairs
// and custom orders are assigned to karigars, payments move money, and every
// balance shown on a page is DERIVED from these linked records — no disconnected
// rows. Dates are generated relative to "today" so the demo always looks current.

const DAY = 24 * 60 * 60 * 1000;

function iso(daysAgo = 0) {
  return new Date(Date.now() - daysAgo * DAY).toISOString();
}
function dateKey(daysAgo = 0) {
  return new Date(Date.now() - daysAgo * DAY).toISOString().slice(0, 10);
}
function uid(prefix) {
  return `${prefix}-${Math.random().toString(16).slice(2, 10)}`;
}
function round(n, d = 0) {
  const f = 10 ** d;
  return Math.round(Number(n || 0) * f) / f;
}
function pick(arr, i) {
  return arr[((i % arr.length) + arr.length) % arr.length];
}

const PURITY_TOUCH = { "24K": 99.9, "22K": 91.6, "18K": 75.0, "14K": 58.5 };
const RATE_24K = 7320; // ₹/g — drives invoice/purchase metal values

// ---------------------------------------------------------------- people pools
const FIRST_NAMES = [
  "Ananya", "Rohan", "Sneha", "Priya", "Karan", "Meera", "Aditya", "Ishita",
  "Vikram", "Divya", "Arjun", "Kavya", "Nikhil", "Pooja", "Sanjay", "Lakshmi",
  "Harish", "Deepa", "Manoj", "Anjali", "Ramesh", "Swathi", "Gautam", "Nisha",
  "Prakash", "Revathi", "Suresh", "Bhavna", "Vivek", "Shruti", "Arvind", "Kiran",
  "Naveen", "Pallavi", "Sandeep", "Aishwarya", "Rahul", "Madhuri", "Vijay", "Geetha",
];
const LAST_NAMES = [
  "Mehta", "Kapoor", "Nair", "Shah", "Bose", "Iyer", "Rao", "Verma",
  "Reddy", "Pillai", "Krishnan", "Menon", "Gupta", "Sharma", "Naidu", "Chettiar",
  "Subramaniam", "Raman", "Balaji", "Srinivasan",
];
const CHENNAI_AREAS = [
  "T. Nagar", "Adyar", "Anna Nagar", "Velachery", "Mylapore", "Nungambakkam",
  "Besant Nagar", "Kilpauk", "Egmore", "Porur", "Ashok Nagar", "Royapettah",
  "Thiruvanmiyur", "Chromepet", "Saidapet", "Alwarpet",
];
const CUSTOMER_TYPES = ["Retail", "Retail", "Retail", "VIP", "Wholesale", "NRI"];

// ---------------------------------------------------------------- customers
function buildCustomers(n = 32) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const name = `${pick(FIRST_NAMES, i)} ${pick(LAST_NAMES, i * 3 + 1)}`;
    const phone = `98${String(40000000 + i * 13337).slice(0, 8)}`;
    const type = pick(CUSTOMER_TYPES, i);
    out.push({
      id: uid("cus"),
      name,
      whatsapp: phone,
      phone,
      email: `${name.toLowerCase().replace(/[^a-z]+/g, ".")}@example.com`,
      address: `${pick(CHENNAI_AREAS, i)}, Chennai`,
      city: "Chennai",
      customer_type: type,
      ring_size: type === "Wholesale" ? "" : String(10 + (i % 12)),
      bangle_size: type === "Wholesale" ? "" : (2.2 + (i % 6) * 0.2).toFixed(1),
      preferences: pick(["22K plain", "Diamond", "Antique / Temple", "Polki", "Daily wear"], i),
      referral: i % 5 === 0 ? pick(FIRST_NAMES, i + 7) : "",
      loyalty_points: round(((i * 137) % 900) + (type === "VIP" ? 1500 : 0)),
      coupon_code: i % 6 === 0 ? `FEST${1000 + i}` : "",
      birthday: i % 4 === 0 ? dateKey(-(((i * 11) % 300) - 150)) : "",
      anniversary: i % 7 === 0 ? dateKey(-(((i * 17) % 300) - 120)) : "",
      // pending_cash / pending_metal / last_billed_date are DERIVED later from
      // the invoices + payments that actually reference this customer.
      pending_cash: 0,
      pending_metal: 0,
      last_billed_date: "",
      is_seed: true,
      created_at: iso(360 - i * 9),
      updated_at: iso(i % 30),
      metadata_json: "{}",
    });
  }
  return out;
}

// ---------------------------------------------------------------- suppliers
const SUPPLIER_DEFS = [
  ["Shree Gold Refinery", "27AABCS1234A1Z5", "Net 30", "metal"],
  ["Kundan Stone House", "24AAACK4567B1Z2", "On settlement", "stone"],
  ["Polki Art Imports", "29AADCP7890C1Z9", "Net 45", "stone"],
  ["Madras Jewel Manufacturing", "33AAECM2345D1Z1", "Net 15", "jewellery"],
  ["Coimbatore Gold Mart", "33AAFCC6789E1Z7", "Advance", "metal"],
  ["Anand Diamond Traders", "27AAGCA3456F1Z4", "Net 30", "stone"],
  ["Lalitha Bangles Works", "33AAHCL8901G1Z2", "Net 21", "jewellery"],
  ["Surana Bullion", "27AAICS5678H1Z8", "Net 7", "metal"],
  ["Temple Craft Karigars", "33AAJCT1234I1Z5", "On settlement", "jewellery"],
  ["Navrathan Gems", "29AAKCN9012J1Z3", "Net 30", "stone"],
];
function buildSuppliers() {
  return SUPPLIER_DEFS.map((s, i) => {
    const [name, gstin, terms, kind] = s;
    const phone = `98${String(99000000 + i * 11111).slice(0, 8)}`;
    return {
      id: uid("sup"),
      name,
      whatsapp: phone,
      phone,
      gstin,
      address: `${pick(CHENNAI_AREAS, i + 2)}, Chennai`,
      payment_terms: terms,
      supplier_kind: kind,
      // derived later from purchases + supplier payments
      pending_cash: 0,
      pending_metal: 0,
      storage: 0,
      last_purchase_date: "",
      is_seed: true,
      created_at: iso(420 - i * 12),
      metadata_json: "{}",
    };
  });
}

// ---------------------------------------------------------------- karigars
const KARIGAR_DEFS = [
  ["Murugan A.", "Setting & Stone work"],
  ["Selvam R.", "Casting"],
  ["Ravi Kumar", "Polishing & Rhodium"],
  ["Bhaskar T.", "Antique / Temple"],
  ["Iqbal S.", "Diamond setting"],
  ["Ganesh P.", "Chain making"],
  ["Velu M.", "Repairs & Resize"],
  ["Suresh N.", "Filigree"],
  ["Karthik V.", "CAD / Custom"],
  ["Dinesh B.", "Engraving"],
];
function buildKarigars() {
  return KARIGAR_DEFS.map((k, i) => {
    const [name, specialization] = k;
    const phone = `97${String(10000000 + i * 23456).slice(0, 8)}`;
    return {
      id: uid("kar"),
      name,
      phone,
      whatsapp: phone,
      specialization,
      // metal ledger derived later from orders/repairs assigned to this karigar
      total_metal_given_fine: 0,
      total_metal_returned_fine: 0,
      pending_metal_fine: 0,
      is_seed: true,
      created_at: iso(500 - i * 20),
      metadata_json: "{}",
    };
  });
}

// ---------------------------------------------------------------- inventory
// Base templates: [name, category(design), item_tab(ItemMaster tab key), purity, gross(g), stoneWt(g)]
// item_tab MUST be one of the ItemMaster tab keys (see lib/itemTabs.js) or the
// page filters it out and the tab shows empty. `category` is the design type.
const ITEM_TEMPLATES = [
  ["22K Gold Ring — Floral", "ring", "gold_jewellery", "22K", 4.2, 0.3],
  ["22K Gold Ring — Classic Band", "ring", "gold_jewellery", "22K", 3.6, 0],
  ["18K Diamond Ring — Solitaire", "ring", "diamond_jewellery", "18K", 3.1, 0.18],
  ["22K Gold Necklace — Temple", "necklace", "gold_jewellery", "22K", 28.4, 1.2],
  ["18K Diamond Necklace — Solitaire", "necklace", "diamond_jewellery", "18K", 12.8, 0.9],
  ["22K Gold Necklace — Antique", "necklace", "gold_jewellery", "22K", 34.7, 2.1],
  ["22K Bangle Set — Antique (pair)", "bangle", "gold_jewellery", "22K", 46.1, 0],
  ["22K Bangle — Plain (single)", "bangle", "gold_jewellery", "22K", 18.9, 0],
  ["18K Polki Bangle", "bangle", "gold_jewellery", "18K", 22.3, 1.6],
  ["22K Gold Earrings — Jhumka", "earring", "gold_jewellery", "22K", 9.4, 0.4],
  ["18K Polki Earrings", "earring", "gold_jewellery", "18K", 8.4, 0.9],
  ["22K Gold Studs — Daily wear", "earring", "gold_jewellery", "22K", 2.8, 0],
  ['22K Rope Chain — 20"', "chain", "gold_jewellery", "22K", 15.6, 0],
  ['22K Box Chain — 18"', "chain", "gold_jewellery", "22K", 11.2, 0],
  ["22K Gold Pendant — Ganesha", "pendant", "gold_jewellery", "22K", 6.7, 0.2],
  ["18K Diamond Pendant — Heart", "pendant", "diamond_jewellery", "18K", 3.9, 0.22],
  ["22K Gold Mangalsutra", "necklace", "gold_jewellery", "22K", 19.5, 0.3],
  ["22K Gold Nose Pin", "nosepin", "gold_jewellery", "22K", 0.9, 0.1],
  ["22K Gold Toe Rings (pair)", "ring", "gold_jewellery", "22K", 5.1, 0],
  ["22K Gold Kada — Gents", "bangle", "gold_jewellery", "22K", 31.2, 0],
  ["18K Diamond Bracelet — Tennis", "bracelet", "diamond_jewellery", "18K", 14.6, 0.6],
  ["22K Gold Bracelet — Filigree", "bracelet", "gold_jewellery", "22K", 12.0, 0.5],
  ["22K Gold Anklet (pair)", "anklet", "gold_jewellery", "22K", 24.8, 0],
  ["18K Lab Diamond Ring — Halo", "ring", "lab_diamond_jewellery", "18K", 3.4, 0.26],
  ["18K Lab Diamond Earrings", "earring", "lab_diamond_jewellery", "18K", 2.6, 0.30],
  ["24K Gold Coin — 10g", "coin", "gold_bullion", "24K", 10.0, 0],
  ["24K Gold Coin — 5g", "coin", "gold_bullion", "24K", 5.0, 0],
  ["24K Gold Bar — 50g", "bar", "gold_bullion", "24K", 50.0, 0],
  ["925 Silver Anklet (pair)", "anklet", "silver_jewellery", "Silver", 38.0, 0],
  ["925 Silver Pooja Lamp", "article", "silver_jewellery", "Silver", 124.0, 0],
  ["999 Silver Coin — 50g", "coin", "silver_bullion", "Silver", 50.0, 0],
  ["999 Silver Bar — 100g", "bar", "silver_bullion", "Silver", 100.0, 0],
  ["Platinum Band — Gents", "ring", "platinum_jewellery", "Platinum", 6.4, 0],
  ["Platinum Couple Rings (pair)", "ring", "platinum_jewellery", "Platinum", 11.2, 0],
  ["22K Gold Haram — Bridal", "necklace", "gold_jewellery", "22K", 58.6, 3.4],
  ["18K Diamond Studs", "earring", "diamond_jewellery", "18K", 2.2, 0.34],
  ["22K Gold Baby Bangles (pair)", "bangle", "gold_jewellery", "22K", 8.2, 0],
  ["Loose Diamond — 0.50ct VS", "loose_stone", "stones", "Stone", 0.1, 0.1],
  ["Loose Emerald — 1.2ct", "loose_stone", "stones", "Stone", 0.24, 0.24],
];

function buildInventory(suppliers, perTemplate = 6) {
  const rows = [];
  let tag = 1001;
  for (let t = 0; t < ITEM_TEMPLATES.length; t++) {
    const [baseName, category, item_tab, purity, baseGross, baseStone] = ITEM_TEMPLATES[t];
    const isBullion = item_tab === "gold_bullion" || item_tab === "silver_bullion";
    const isStoneTab = item_tab === "stones";
    for (let v = 0; v < perTemplate; v++) {
      const idx = rows.length;
      const factor = 1 + ((v - 2) * 0.06); // small weight spread per variation
      const gross = round(baseGross * factor, 3);
      const stoneWt = round(baseStone * factor, 3);
      const net = Math.max(0, round(gross - stoneWt, 3));
      const isMetal = !(purity === "Silver" || purity === "Platinum" || purity === "Stone");
      const making = Math.round(net * (isBullion ? 60 : 350));
      const stoneCharges = isStoneTab
        ? Math.round(18000 + (idx % 6) * 9000)
        : stoneWt > 0
        ? Math.round(stoneWt * 2500)
        : 0;
      const supplier = pick(suppliers, idx * 7 + 1);
      // Status mix: most available; a slice sold/approval/repair so every status
      // and the linked pages have something to show. Bullion/stones never repair.
      let status = "available";
      const m = idx % 9;
      if (m === 1 || m === 4) status = "sold";
      else if (m === 6) status = "approval";
      else if (m === 8 && !isBullion && !isStoneTab) status = "repair";
      rows.push({
        id: uid("itm"),
        tag_no: String(tag++),
        item_name: `${baseName}${v ? ` #${v + 1}` : ""}`,
        item_tab,
        category,
        item_type: category,
        gross_weight: gross,
        cz_weight: 0,
        moti_weight: 0,
        kundan_weight: 0,
        beads_weight: 0,
        stone_weight: stoneWt,
        net_weight: net,
        purity,
        touch: PURITY_TOUCH[purity] || (isMetal ? 91.6 : 0),
        wastage: isMetal ? round(net * 0.08, 3) : 0,
        stone_charges: stoneCharges,
        making_charges: making,
        remarks: "",
        og_tag: `OG-${supplier.id.slice(-4).toUpperCase()}-${tag}`,
        supplier_tag: `${supplier.name.split(" ")[0].toUpperCase()}/${1000 + idx}`,
        box_no: `B-${(idx % 8) + 1}`,
        storage_location: `Showcase ${(idx % 5) + 1}`,
        status,
        supplier_id: supplier.id,
        supplier_name: supplier.name,
        purchase_supplier_id: supplier.id,
        // sold-item links filled in by linkSoldItems() once invoices exist
        sold_customer_id: "",
        sold_customer_name: "",
        sold_invoice_number: "",
        photo_url: "",
        sold_at: status === "sold" ? iso((idx % 40) + 1) : null,
        created_at: iso(300 - idx),
        updated_at: iso(idx % 20),
        metadata_json: "{}",
      });
    }
  }
  return rows;
}

// ---------------------------------------------------------------- invoices / estimates
// estimates store holds BOTH estimates (draft/expired) and invoices (paid/pending).
function buildInvoices(customers, inventory) {
  const rows = [];
  const sellable = inventory.filter((i) => i.status === "sold" || i.status === "available");
  let sellPtr = 0;
  const total = 62;
  for (let i = 0; i < total; i++) {
    const cust = pick(customers, i * 5 + 3);
    const daysAgo = Math.round((i / total) * 330) + (i % 7);
    // pick 1–2 items for this bill
    const lineCount = 1 + (i % 2);
    const items = [];
    let fine = 0;
    let metalValue = 0;
    let makingValue = 0;
    for (let l = 0; l < lineCount; l++) {
      const it = sellable[(sellPtr++) % sellable.length];
      const itemFine = round(it.net_weight * ((PURITY_TOUCH[it.purity] || 91.6) / 100), 3);
      const metal = Math.round(itemFine * RATE_24K);
      const making = it.making_charges + it.stone_charges;
      fine += itemFine;
      metalValue += metal;
      makingValue += making;
      items.push({
        item_name: it.item_name,
        tag_no: it.tag_no,
        qty: 1,
        net_weight: it.net_weight,
        purity: it.purity,
        rate: RATE_24K,
        making_charges: it.making_charges,
        stone_charges: it.stone_charges,
        amount: metal + making,
      });
    }
    const subtotal = metalValue + makingValue;
    const discount = i % 6 === 0 ? Math.round(subtotal * 0.03) : 0;
    const taxable = subtotal - discount;
    const gst = Math.round(taxable * 0.03);
    const grand = taxable + gst;

    // status mix: drafts (estimates), converted, paid, pending(due)
    let status;
    const mod = i % 10;
    if (mod < 3) status = "draft";
    else if (mod < 5) status = "converted";
    else if (mod < 8) status = "paid";
    else status = "pending";
    const isInvoice = status !== "draft" && status !== "expired";
    if (i % 17 === 16) status = "expired";

    rows.push({
      id: uid("inv"),
      invoice_number: isInvoice ? `INV-${3200 + i}` : `EST-${2400 + i}`,
      doc_type: isInvoice ? "invoice" : "estimate",
      customer_id: cust.id,
      customer_name: cust.name,
      customer_whatsapp: cust.whatsapp,
      invoice_date: dateKey(daysAgo),
      created_at: iso(daysAgo),
      subtotal,
      discount,
      gst_amount: gst,
      grand_total: grand,
      total_with_gst: grand,
      total_fine: round(fine, 3),
      invoice_metal_component: round(fine, 3),
      payment_mode: pick(["Cash", "UPI", "Card", "Bank", "Cash"], i),
      status,
      items,
      items_json: JSON.stringify(items),
    });
  }
  return rows;
}

// ---------------------------------------------------------------- repairs
const REPAIR_DEFS = [
  ["Ring resize +1", "in_progress", 600],
  ["Chain solder", "ready", 350],
  ["Stone re-setting", "pending", 1200],
  ["Polish & rhodium", "in_progress", 900],
  ["Clasp replacement", "collected", 500],
  ["Bangle re-shape", "pending", 750],
  ["Earring post repair", "ready", 300],
  ["Necklace restringing", "in_progress", 1400],
  ["Engraving", "pending", 450],
  ["Rhodium plating", "collected", 650],
];
function buildRepairs(customers, karigars, inventory) {
  const repairItems = inventory.filter((i) => i.status === "repair");
  const rows = [];
  const total = 30;
  for (let i = 0; i < total; i++) {
    const [item_name, status, baseAmt] = pick(REPAIR_DEFS, i);
    const cust = pick(customers, i * 3 + 2);
    const kar = pick(karigars, i + 1);
    const linkedItem = repairItems.length ? pick(repairItems, i) : null;
    const daysAgo = (i % 25) + 1;
    const amount = baseAmt + (i % 5) * 100;
    rows.push({
      id: uid("rpr"),
      job_no: `RPR-${1041 + i}`,
      customer_id: cust.id,
      customer_name: cust.name,
      whatsapp: cust.whatsapp,
      repair_type: "repair",
      item_name,
      item_id: linkedItem ? linkedItem.id : "",
      item_tag: linkedItem ? linkedItem.tag_no : "",
      items_json: "[]",
      description: item_name,
      estimate_date: dateKey(daysAgo),
      due_date: dateKey(daysAgo - 5),
      customer_given_weight: round(2 + (i % 6) * 0.6, 2),
      karigar_id: kar.id,
      karigar_name: kar.name,
      amount,
      estimate_cost: amount,
      final_cost: ["collected", "ready"].includes(status) ? amount : 0,
      advance: Math.round(amount / 2),
      status,
      photos_json: "[]",
      created_at: iso(daysAgo),
    });
  }
  return rows;
}

// ---------------------------------------------------------------- orders (work / karigar orders)
const ORDER_DEFS = [
  ["Custom mangalsutra", "in_production", 19.5],
  ["Engagement ring — solitaire", "design_approval", 3.4],
  ["Bangle pair — antique", "ready", 46.0],
  ["Temple necklace — bridal", "pending", 52.0],
  ["Gents kada", "in_production", 31.0],
  ["Diamond pendant set", "design_approval", 7.8],
  ["Baby anklets (pair)", "ready", 16.4],
  ["Polki choker", "pending", 38.2],
];
function buildOrders(customers, karigars) {
  const rows = [];
  const total = 28;
  for (let i = 0; i < total; i++) {
    const [item_description, status, estimate_weight] = pick(ORDER_DEFS, i);
    const cust = pick(customers, i * 4 + 1);
    const kar = pick(karigars, i + 3);
    const daysAgo = (i % 30) + 1;
    const fine = round(estimate_weight * 0.916, 3);
    const amount = Math.round(fine * RATE_24K + estimate_weight * 450);
    rows.push({
      id: uid("ord"),
      work_order_no: `ORD-${2207 + i}`,
      customer_id: cust.id,
      customer_name: cust.name,
      item_description,
      item_name: item_description,
      size: "",
      purity: 91.6,
      estimate_weight,
      net_weight: estimate_weight,
      karigar_id: kar.id,
      karigar_name: kar.name,
      order_date: dateKey(daysAgo),
      expected_completion: dateKey(daysAgo - 18),
      due_date: dateKey(daysAgo - 18),
      amount,
      advance: Math.round(amount * 0.4),
      status,
      created_at: iso(daysAgo),
    });
  }
  return rows;
}

// ---------------------------------------------------------------- purchases
function buildPurchases(suppliers, inventory) {
  const rows = [];
  const total = 32;
  for (let i = 0; i < total; i++) {
    const sup = pick(suppliers, i * 3 + 1);
    const daysAgo = Math.round((i / total) * 300) + (i % 9);
    const isStone = sup.supplier_kind === "stone" || i % 4 === 3;
    const purchase_type = isStone ? "stone" : "jewellery";
    // value
    const fine = isStone ? 0 : round(20 + (i % 8) * 6.5, 3);
    const metalValue = Math.round(fine * RATE_24K);
    const stoneValue = isStone ? Math.round(15000 + (i % 6) * 8500) : Math.round((i % 3) * 4000);
    const total_amount = metalValue + stoneValue + Math.round((i % 5) * 1200);

    // status + balances
    let status, metal_balance;
    const mod = i % 6;
    if (mod === 0) status = "settled";
    else if (mod === 1) status = "rate_fixed";
    else if (mod === 5 && i > 20) status = "returned";
    else status = "pending";
    metal_balance = status === "pending" || status === "rate_fixed" ? fine : 0;
    const paid_amount = status === "settled" ? total_amount : status === "rate_fixed" ? Math.round(total_amount * 0.5) : 0;

    rows.push({
      id: uid("pur"),
      purchase_number: `PUR-${4100 + i}`,
      tab_key: purchase_type,
      purchase_type,
      supplier_id: sup.id,
      supplier_name: sup.name,
      purchase_date: dateKey(daysAgo),
      total_amount,
      fine_weight: fine,
      metal_balance: status === "settled" || status === "returned" ? 0 : metal_balance,
      paid_amount,
      cash_balance: status === "settled" || status === "returned" ? 0 : total_amount - paid_amount,
      status,
      items: [
        {
          item_name: isStone ? "Loose stones lot" : pick(["Gold jewellery lot", "Bangles lot", "Chains lot"], i),
          net_weight: fine ? round(fine / 0.916, 3) : 0,
          fine_weight: fine,
          amount: total_amount,
        },
      ],
      created_at: iso(daysAgo),
    });
  }
  return rows;
}

// ---------------------------------------------------------------- consignment / approval memos
function buildConsignment(suppliers, inventory) {
  const approvalItems = inventory.filter((i) => i.status === "approval");
  const rows = [];
  const total = 10;
  let itemPtr = 0;
  for (let i = 0; i < total; i++) {
    const sup = pick(suppliers.filter((s) => s.supplier_kind !== "metal"), i) || pick(suppliers, i);
    const daysAgo = (i % 60) + 5;
    const itemCount = 3 + (i % 4);
    const memoItems = [];
    let totalWt = 0;
    let soldWt = 0;
    let remainingWt = 0;
    // phase drives the memo lifecycle so the page shows real variety:
    // active (nothing sold yet) / settled (all sold) / partially_settled (mixed).
    const phase = i % 4; // 0=active, 1=settled, else mixed
    for (let k = 0; k < itemCount; k++) {
      const src = approvalItems.length ? approvalItems[(itemPtr++) % approvalItems.length] : null;
      const wt = src ? src.net_weight : round(8 + k * 2.5, 3);
      let st;
      if (phase === 0) st = "on_approval";
      else if (phase === 1) st = "sold";
      else st = k % 3 === 0 ? "sold" : k % 3 === 1 ? "on_approval" : "returned";
      totalWt += wt;
      if (st === "sold") soldWt += wt;
      else if (st === "on_approval") remainingWt += wt;
      memoItems.push({
        id: uid("citm"),
        item_name: src ? src.item_name : `Approval piece ${k + 1}`,
        tag_no: src ? src.tag_no : String(9000 + i * 10 + k),
        net_weight: wt,
        status: st,
      });
    }
    let status;
    if (soldWt > 0 && remainingWt === 0) status = "settled";
    else if (soldWt > 0) status = "partially_settled";
    else status = "active";
    const fine = round(remainingWt * 0.916, 3);
    const estimated_payable = Math.round(soldWt * 0.916 * RATE_24K);
    const interest_enabled = i % 3 === 0;
    rows.push({
      id: uid("memo"),
      memo_number: `APR-${700 + i}`,
      supplier_id: sup.id,
      supplier_name: sup.name,
      received_date: dateKey(daysAgo),
      status,
      item_count: itemCount,
      items: memoItems,
      total_sold_weight: round(soldWt, 3),
      total_remaining_weight: round(remainingWt, 3),
      active_fine_weight: fine,
      days_held: daysAgo,
      estimated_payable,
      interest_enabled,
      interest_preview: interest_enabled ? Math.round(estimated_payable * 0.01 * (daysAgo / 30)) : 0,
      created_at: iso(daysAgo),
    });
  }
  return rows;
}

// ---------------------------------------------------------------- payments (linked)
function buildPayments(customers, suppliers, invoices, purchases) {
  const rows = [];
  // Customer receipts for paid invoices.
  invoices
    .filter((inv) => inv.status === "paid")
    .forEach((inv, i) => {
      rows.push({
        id: uid("pay"),
        party_type: "customer",
        party_id: inv.customer_id,
        party_name: inv.customer_name,
        ref_type: "invoice",
        ref_id: inv.id,
        ref_no: inv.invoice_number,
        date: inv.invoice_date,
        cash_amount: inv.grand_total,
        metal_fine: 0,
        mode: inv.payment_mode,
        note: `Receipt for ${inv.invoice_number}`,
        created_at: inv.created_at,
      });
    });
  // Supplier payments for settled purchases (+ partial for rate_fixed).
  purchases.forEach((pur, i) => {
    if (pur.status === "settled") {
      rows.push({
        id: uid("pay"),
        party_type: "supplier",
        party_id: pur.supplier_id,
        party_name: pur.supplier_name,
        ref_type: "purchase",
        ref_id: pur.id,
        ref_no: pur.purchase_number,
        date: pur.purchase_date,
        cash_amount: pur.total_amount,
        metal_fine: 0,
        mode: pick(["Bank", "UPI", "Cash"], i),
        note: `Paid ${pur.purchase_number}`,
        created_at: pur.created_at,
      });
    } else if (pur.status === "rate_fixed" && pur.paid_amount > 0) {
      rows.push({
        id: uid("pay"),
        party_type: "supplier",
        party_id: pur.supplier_id,
        party_name: pur.supplier_name,
        ref_type: "purchase",
        ref_id: pur.id,
        ref_no: pur.purchase_number,
        date: pur.purchase_date,
        cash_amount: pur.paid_amount,
        metal_fine: 0,
        mode: "Bank",
        note: `Part payment ${pur.purchase_number}`,
        created_at: pur.created_at,
      });
    }
  });
  return rows;
}

// ---------------------------------------------------------------- expenses (petty-cash wallet)
// Wallet users — petty cash is held/spent by these staff accounts.
const EXPENSE_USERS = [
  { user_account_id: "u-counter", display_name: "Counter Desk", full_name: "Counter Desk", email: "counter@jewelflow.demo" },
  { user_account_id: "u-manager", display_name: "Store Manager", full_name: "Store Manager", email: "manager@jewelflow.demo" },
  { user_account_id: "u-owner", display_name: "Owner", full_name: "Owner", email: "owner@jewelflow.demo" },
];
const EXPENSE_DEFS = [
  ["Rent", "Shop rent — monthly"],
  ["Salary", "Staff salary"],
  ["Electricity", "EB bill"],
  ["Marketing", "Festival ad campaign"],
  ["Packaging", "Boxes & pouches"],
  ["Hallmarking", "BIS hallmarking charges"],
  ["Courier", "Insured courier"],
  ["Tea & Snacks", "Pantry & refreshments"],
  ["Insurance", "Stock insurance premium"],
  ["Maintenance", "AC & CCTV service"],
  ["Transport", "Auto / cab for bank run"],
  ["Office", "Stationery & printer ink"],
  ["Misc", "Temple donation / sundry"],
];
function buildExpenses() {
  const rows = [];
  // 60 expense (debit) transactions across categories + users.
  for (let i = 0; i < 60; i++) {
    const [category, description] = pick(EXPENSE_DEFS, i);
    const daysAgo = (i % 56) + 1;
    const usr = pick(EXPENSE_USERS, i);
    const amount = Math.round(450 + (i % 14) * 1280 + (category === "Salary" ? 22000 : category === "Rent" ? 45000 : 0));
    rows.push({
      id: uid("exp"),
      transaction_type: "expense",
      type: "debit",
      date: dateKey(daysAgo),
      created_at: iso(daysAgo),
      category,
      custom_category: "",
      title: description,
      description,
      notes: description,
      amount,
      payment_mode: pick(["Cash", "UPI", "Bank", "Card"], i),
      mode: pick(["Cash", "UPI", "Bank", "Card"], i),
      user_account_id: usr.user_account_id,
      expense_by: usr.display_name,
      created_by_name: usr.display_name,
      reference_no: `EXP-${5200 + i}`,
      status: "approved",
      bill_image: "",
      bill_preview: "",
    });
  }
  // "Funds added" (credit) entries — seeded to total slightly MORE than the
  // expenses above so the wallet always shows a healthy positive petty-cash
  // balance after a reset (instead of a confusing negative number).
  const totalDebits = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const fundsTotal = Math.round(totalDebits + 85000); // running buffer
  const fundEntries = 6;
  for (let i = 0; i < fundEntries; i++) {
    const daysAgo = 60 - i * 9;
    const usr = pick(EXPENSE_USERS, i);
    // Distribute the total across the entries (last one absorbs the remainder).
    const amount =
      i === fundEntries - 1
        ? fundsTotal - Math.round(fundsTotal / fundEntries) * (fundEntries - 1)
        : Math.round(fundsTotal / fundEntries);
    rows.push({
      id: uid("exp"),
      transaction_type: "funds",
      type: "credit",
      date: dateKey(daysAgo),
      created_at: iso(daysAgo),
      category: "Funds",
      custom_category: "",
      title: "Petty cash top-up",
      description: "Petty cash top-up",
      notes: "Petty cash top-up",
      amount,
      payment_mode: "Cash",
      mode: "Cash",
      user_account_id: usr.user_account_id,
      expense_by: usr.display_name,
      created_by_name: "Owner",
      reference_no: `FND-${320 + i}`,
      status: "approved",
      bill_image: "",
      bill_preview: "",
    });
  }
  return rows;
}

// ---------------------------------------------------------------- bulk inventory (weight/qty buckets)
// Buckets track loose stock by weight or piece count; each has a ledger whose
// signed quantities sum to the bucket balance. Some buckets carry an item_tab so
// the per-tab BulkStockPanel inside Item Master shows them too.
const BULK_BUCKET_DEFS = [
  // name, unit, purity, metal, category, item_tab
  ["22K Loose Chains", "grams", "22K", "gold", "chains", "gold_jewellery"],
  ["22K Generic Rings", "grams", "22K", "gold", "rings", "gold_jewellery"],
  ["Silver Articles", "grams", "925", "silver", "articles", "silver_jewellery"],
  ["Silver Coins", "pieces", "999", "silver", "coins", "silver_bullion"],
  ["24K Gold Stock", "grams", "24K", "gold", "bullion", "gold_bullion"],
  ["Findings & Components", "pieces", "", "gold", "findings", ""],
  ["Loose CZ Stones", "carats", "", "none", "stones", "stones"],
  ["Gold Casting Grain", "grams", "22K", "gold", "raw", ""],
];
const BULK_TXN_TYPES = [
  ["opening", 1],
  ["purchase", 1],
  ["sale", -1],
  ["return", 1],
  ["adjustment", 1],
];
function buildBulkInventory(suppliers) {
  const buckets = [];
  const ledger = [];
  for (let i = 0; i < BULK_BUCKET_DEFS.length; i++) {
    const [name, unit, purity, metal, category, item_tab] = BULK_BUCKET_DEFS[i];
    const bucketId = uid("bkt");
    const base = unit === "pieces" ? 30 : unit === "carats" ? 120 : 480;
    // Build a small ledger; balance is the signed sum.
    let balance = 0;
    const entryCount = 4 + (i % 4);
    for (let k = 0; k < entryCount; k++) {
      const [txn_type, dir] = pick(BULK_TXN_TYPES, k === 0 ? 0 : i + k); // first entry = opening
      const daysAgo = (entryCount - k) * 11 + (i % 5);
      const magnitude =
        txn_type === "opening"
          ? base
          : unit === "pieces"
          ? 2 + (k % 5)
          : round(base * (0.04 + (k % 5) * 0.03), 3);
      const qty = round(magnitude * dir, 3);
      balance = round(balance + qty, 3);
      const sup = pick(suppliers, i + k);
      ledger.push({
        id: uid("blg"),
        bucket_id: bucketId,
        txn_type,
        quantity: qty,
        unit,
        txn_date: dateKey(daysAgo),
        notes:
          txn_type === "purchase"
            ? `From ${sup.name}`
            : txn_type === "sale"
            ? "Counter sale"
            : txn_type === "opening"
            ? "Opening balance"
            : "",
        supplier_id: txn_type === "purchase" ? sup.id : "",
        created_at: iso(daysAgo),
      });
    }
    if (balance < 0) balance = round(base * 0.5, 3); // never seed a negative opening demo
    buckets.push({
      id: bucketId,
      name,
      unit,
      balance,
      group_by_purity: purity || null,
      group_by_metal: metal === "none" ? null : metal,
      group_by_category: category || null,
      item_tab: item_tab || null,
      allow_negative: false,
      notes: "",
      created_at: iso(120 + i * 8),
    });
  }
  return { buckets, ledger };
}

// ---------------------------------------------------------------- purchase returns
const RETURN_REASONS = [
  "Wrong purity received",
  "Damaged on inspection",
  "Excess weight billed",
  "Quality not as approved",
  "Wrong design supplied",
  "Customer cancelled custom order",
];
function buildPurchaseReturns(suppliers, purchases) {
  const rows = [];
  const total = 22;
  for (let i = 0; i < total; i++) {
    // Prefer linking to a real purchase so the return references a known PO.
    const linked = purchases.length ? pick(purchases, i * 3 + 1) : null;
    const sup = linked
      ? suppliers.find((s) => s.id === linked.supplier_id) || pick(suppliers, i)
      : pick(suppliers, i);
    const daysAgo = (i % 50) + 2;
    const fine = round(2 + (i % 9) * 1.4, 3);
    const cash = Math.round(fine * RATE_24K + (i % 4) * 3500);
    const status = pick(["completed", "approved", "completed", "draft"], i);
    const id = uid("pret");
    rows.push({
      id,
      return_id: id,
      return_no: `PRTN-${900 + i}`,
      return_number: `PRTN-${900 + i}`,
      ledger_id: uid("sled"),
      date: dateKey(daysAgo),
      return_date: dateKey(daysAgo),
      supplier_id: sup.id,
      supplier_name: sup.name,
      linked_purchase_number: linked ? linked.purchase_number : "",
      linked_purchase_numbers: linked ? [linked.purchase_number] : [],
      tags: linked && linked.items?.[0] ? [linked.items[0].item_name] : [],
      reason: pick(RETURN_REASONS, i),
      quantity: 1 + (i % 3),
      weight: fine,
      fine_weight: fine,
      metal_returned: fine,
      cash_returned: cash,
      total_amount: cash,
      status,
      created_by_name: pick(["Owner", "Store Manager", "Counter Desk"], i),
      notes: pick(RETURN_REASONS, i),
      created_at: iso(daysAgo),
    });
  }
  return rows;
}

// ---------------------------------------------------------------- pipeline leads
const LEAD_STAGES = ["new", "contacted", "quoted", "negotiation", "won", "lost"];
function buildPipeline(customers) {
  const rows = [];
  for (let i = 0; i < 12; i++) {
    const cust = pick(customers, i * 2 + 5);
    const stage = pick(LEAD_STAGES, i);
    const daysAgo = (i % 20) + 1;
    rows.push({
      id: uid("lead"),
      name: cust.name,
      whatsapp: cust.whatsapp,
      customer_id: cust.id,
      stage,
      value: Math.round(40000 + (i % 8) * 22000),
      source: pick(["Walk-in", "Referral", "Instagram", "WhatsApp", "Wedding expo"], i),
      interest: pick(["Bridal set", "Diamond ring", "Daily wear", "Gold coins", "Mangalsutra"], i),
      expected_close: dateKey(daysAgo - 14),
      created_at: iso(daysAgo),
    });
  }
  return rows;
}

// ---------------------------------------------------------------- gold rate (+ history)
function buildGoldRate() {
  const r24 = RATE_24K;
  return {
    id: "current",
    rate_per_gram: Math.round(r24 * 0.916),
    rate_24k: r24,
    rate_22k: Math.round(r24 * 0.916),
    rate_18k: Math.round(r24 * 0.75),
    rate_14k: Math.round(r24 * 0.585),
    silver_per_kg: 92500,
    rate_mode: "derive_24k",
    date: dateKey(0),
    updated_at: iso(0),
  };
}
function buildGoldRateHistory(days = 30) {
  const out = [];
  for (let d = days; d >= 0; d--) {
    // gentle wander around the base rate
    const wobble = Math.round(Math.sin(d / 3) * 60 + ((d % 5) - 2) * 18);
    const r24 = RATE_24K - 90 + Math.round(((days - d) / days) * 120) + wobble;
    out.push({
      id: dateKey(d),
      date: dateKey(d),
      rate_24k: r24,
      rate_22k: Math.round(r24 * 0.916),
      rate_18k: Math.round(r24 * 0.75),
    });
  }
  return out;
}

// ---------------------------------------------------------------- dashboard tasks
function buildTasks(customers) {
  const defs = [
    ["Call about pendant repair pickup", 0, "high"],
    ["Confirm bridal set design", 1, "high"],
    ["Follow up supplier payment — Shree Gold", 1, "medium"],
    ["Festival coupon batch — send to top customers", 2, "medium"],
    ["Low stock: 24K coins running low", 0, "high"],
    ["Approval return due — Kundan Stone House", 3, "medium"],
  ];
  return defs.map((d, i) => {
    const id = uid("task");
    const cust = pick(customers, i * 6);
    const title = i < 2 ? `${d[0].split(" ")[0]} ${cust.name} — ${d[0]}` : d[0];
    return {
      id,
      _id: id,
      title,
      text: title,
      date: dateKey(-d[1]),
      category: "work",
      priority: d[2],
      status: "pending",
      created_at: iso(d[1]),
    };
  });
}

// ---------------------------------------------------------------- analytics
function buildWeeklySales(invoices) {
  const out = [];
  for (let d = 6; d >= 0; d--) {
    const key = dateKey(d);
    const dayInvoices = invoices.filter((inv) => inv.invoice_date === key && inv.doc_type === "invoice");
    const base = [82000, 41000, 96000, 120000, 64000, 152000, 188000][6 - d] || 60000;
    const total = base + dayInvoices.reduce((s, inv) => s + Number(inv.grand_total || 0), 0);
    out.push({
      date: key,
      total,
      sales: total,
      fineSold: round(total / 7300, 2),
      fine_sold: round(total / 7300, 2),
      bills: 2 + ((6 - d) % 3) + dayInvoices.length,
    });
  }
  return out;
}
function buildMonthlySales(invoices) {
  const out = [];
  const now = new Date();
  for (let m = 11; m >= 0; m--) {
    const d = new Date(now.getFullYear(), now.getMonth() - m, 1);
    const key = d.toISOString().slice(0, 7);
    const monthInvoices = invoices.filter(
      (inv) => inv.doc_type === "invoice" && String(inv.invoice_date).slice(0, 7) === key
    );
    const seasonal = 380000 + Math.round(Math.sin((11 - m) / 2) * 120000) + (11 - m) * 14000;
    const total = seasonal + monthInvoices.reduce((s, inv) => s + Number(inv.grand_total || 0), 0);
    out.push({
      month: key,
      label: d.toLocaleString("en-IN", { month: "short" }),
      total,
      sales: total,
      bills: 18 + ((11 - m) % 6) + monthInvoices.length,
    });
  }
  return out;
}

// ---------------------------------------------------------------- link sold items
// Attach each "sold" inventory item to a real invoice + customer so Item Master's
// Sold tab, margins, and profit/loss all reconcile against the same transactions.
function linkSoldItems(invoices, inventory) {
  const soldItems = inventory.filter((i) => i.status === "sold");
  const realInvoices = invoices.filter((inv) => inv.doc_type === "invoice");
  if (!realInvoices.length) return;
  soldItems.forEach((it, idx) => {
    const inv = realInvoices[idx % realInvoices.length];
    it.sold_customer_id = inv.customer_id;
    it.sold_customer_name = inv.customer_name;
    it.sold_invoice_number = inv.invoice_number;
    if (!it.sold_at) it.sold_at = iso((idx % 40) + 1);
  });
}

// ---------------------------------------------------------------- derive balances
// Make every party balance equal the sum of the records that reference it.
function applyDerivedBalances({ customers, suppliers, karigars, invoices, purchases, repairs, orders, purchaseReturns }) {
  const cust = new Map(customers.map((c) => [c.id, c]));
  invoices.forEach((inv) => {
    const c = cust.get(inv.customer_id);
    if (!c) return;
    if (!c.last_billed_date || inv.invoice_date > c.last_billed_date) c.last_billed_date = inv.invoice_date;
    if (inv.status === "pending") {
      c.pending_cash += Number(inv.grand_total || 0);
      c.pending_metal += Number(inv.total_fine || 0) * 0.15; // small metal-due slice
    }
  });
  customers.forEach((c) => {
    c.pending_cash = round(c.pending_cash, 0);
    c.pending_metal = round(c.pending_metal, 3);
  });

  const sup = new Map(suppliers.map((s) => [s.id, s]));
  purchases.forEach((p) => {
    const s = sup.get(p.supplier_id);
    if (!s) return;
    if (!s.last_purchase_date || p.purchase_date > s.last_purchase_date) s.last_purchase_date = p.purchase_date;
    if (p.status === "pending" || p.status === "rate_fixed") {
      s.pending_cash += Number(p.cash_balance || 0);
      s.pending_metal += Number(p.metal_balance || 0);
    }
  });
  // Standalone purchase returns reduce what we owe the supplier (cash + metal),
  // floored at zero so a demo supplier never shows a negative due.
  (purchaseReturns || []).forEach((r) => {
    if (r.status === "draft") return;
    const s = sup.get(r.supplier_id);
    if (!s) return;
    s.pending_cash = Math.max(0, Number(s.pending_cash || 0) - Number(r.cash_returned || 0));
    s.pending_metal = Math.max(0, Number(s.pending_metal || 0) - Number(r.metal_returned || 0));
  });
  suppliers.forEach((s) => {
    s.pending_cash = round(s.pending_cash, 0);
    s.pending_metal = round(s.pending_metal, 3);
  });

  // karigar metal ledger from assigned orders + repairs
  const kar = new Map(karigars.map((k) => [k.id, k]));
  orders.forEach((o) => {
    const k = kar.get(o.karigar_id);
    if (!k) return;
    const givenFine = round(Number(o.estimate_weight || 0) * 0.916, 3);
    k.total_metal_given_fine += givenFine;
    if (["ready", "delivered", "completed"].includes(o.status)) k.total_metal_returned_fine += givenFine;
  });
  repairs.forEach((r) => {
    const k = kar.get(r.karigar_id);
    if (!k) return;
    const givenFine = round(Number(r.customer_given_weight || 0) * 0.916, 3);
    k.total_metal_given_fine += givenFine;
    if (["ready", "collected", "delivered"].includes(r.status)) k.total_metal_returned_fine += givenFine;
  });
  karigars.forEach((k) => {
    k.total_metal_given_fine = round(k.total_metal_given_fine, 3);
    k.total_metal_returned_fine = round(k.total_metal_returned_fine, 3);
    k.pending_metal_fine = round(k.total_metal_given_fine - k.total_metal_returned_fine, 3);
  });
}

// ---------------------------------------------------------------- assemble
export function buildSeed() {
  const customers = buildCustomers(32);
  const suppliers = buildSuppliers();
  const karigars = buildKarigars();
  const inventory = buildInventory(suppliers, 6);
  const invoices = buildInvoices(customers, inventory);
  const repairs = buildRepairs(customers, karigars, inventory);
  const orders = buildOrders(customers, karigars);
  const purchases = buildPurchases(suppliers, inventory);
  const purchaseReturns = buildPurchaseReturns(suppliers, purchases);
  const consignmentMemos = buildConsignment(suppliers, inventory);
  const payments = buildPayments(customers, suppliers, invoices, purchases);
  const expenses = buildExpenses();
  const { buckets: bulkBuckets, ledger: bulkLedger } = buildBulkInventory(suppliers);
  const pipelineLeads = buildPipeline(customers);
  const goldRate = buildGoldRate();
  const goldRateHistory = buildGoldRateHistory(30);
  const tasks = buildTasks(customers);

  linkSoldItems(invoices, inventory);
  applyDerivedBalances({ customers, suppliers, karigars, invoices, purchases, repairs, orders, purchaseReturns });

  const weeklySales = buildWeeklySales(invoices);
  const monthlySales = buildMonthlySales(invoices);

  return {
    customers,
    suppliers,
    karigars,
    inventory,
    estimates: invoices, // estimates + invoices share one store in the demo
    repairs,
    orders,
    purchases,
    purchaseReturns,
    consignmentMemos,
    payments,
    expenses,
    bulkBuckets,
    bulkLedger,
    pipelineLeads,
    goldRates: [goldRate],
    goldRateHistory,
    dashboardTasks: tasks,
    analytics: {
      busiest_hour: "6 PM",
      busiest_weekday: "Saturday",
      weekly_sales: weeklySales,
      monthly_sales: monthlySales,
    },
  };
}
